// Sources: input files attached to a workflow as source1, source2, …
//
// Source definition (saved with the workflow):
//   {
//     id: "source1", label: "Freelancer payments",
//     originalName: "payments_oct.xlsx", sheetName: "Sheet1",
//     hasHeader: true, headerRow: 1,          // 1-based row of the header
//     matchBy: "header" | "position",         // how new files are matched
//     columns: [{ name, letter, header, type }]
//   }
// A column's `name` is what steps refer to: the header text when the file
// has headers, otherwise its letter.
import Papa from "papaparse";
import { columnLetter, hasColumn, letterToIndex, uniqueName } from "./table";
import { inferType, isBlank, normalizeCell, toBoolean, toDate, toNumber, toText } from "./values";

// ---------- Reading files into raw sheets ----------

const trimTrailingBlankRows = (cells) => {
  let end = cells.length;
  while (end > 0 && cells[end - 1].every(isBlank)) end--;
  return cells.slice(0, end);
};

export const parseCsv = (text) => {
  const { data } = Papa.parse(text.replace(/^\uFEFF/, ""), { skipEmptyLines: false, dynamicTyping: false });
  return trimTrailingBlankRows(data.map((row) => row.map(normalizeCell)));
};

// ExcelJS worksheet → 2D array of plain values (0-based rows and columns).
export const worksheetToCells = (worksheet) => {
  const cells = [];
  const width = worksheet.columnCount;
  worksheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
    const values = [];
    for (let col = 1; col <= width; col++) values.push(normalizeCell(row.getCell(col).value));
    cells[rowNumber - 1] = values;
  });
  for (let i = 0; i < cells.length; i++) if (!cells[i]) cells[i] = new Array(width).fill(null);
  return trimTrailingBlankRows(cells);
};

const isCsvName = (name = "") => /\.(csv|txt|tsv)$/i.test(name);

// Reads a File/Blob (browser) or { name, buffer } into
// { fileName, sheets: [{ name, cells }] }. Every Excel sheet is returned so
// the user can attach any of them as a source.
export const readWorkbook = async (file) => {
  const fileName = file.name || "file";
  if (isCsvName(fileName) || file.type === "text/csv") {
    const text = file.text ? await file.text() : new TextDecoder().decode(file.buffer);
    return { fileName, sheets: [{ name: fileName.replace(/\.[^.]+$/, ""), cells: parseCsv(text) }] };
  }
  const { default: ExcelJS } = await import("exceljs");
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(file.arrayBuffer ? await file.arrayBuffer() : file.buffer);
  return {
    fileName,
    sheets: workbook.worksheets.map((worksheet) => ({ name: worksheet.name, cells: worksheetToCells(worksheet) })),
  };
};

// ---------- Describing a sheet ----------

// Text that is data, never a column name: an email, a link, a phone number or
// a long number / id.
const looksLikeData = (text) => /@[\w-]+\.\w|:\/\/|^www\.|\d{6,}/i.test(text) || (text.match(/\d/g) || []).length >= 8;

const looksLikeLabel = (value) =>
  !isBlank(value) && typeof value === "string" && toNumber(value) === null && toDate(value) === null && !looksLikeData(value.trim());

// Guess whether the first non-blank row is a header: all its cells are
// distinct text labels (not emails, phone numbers or ids), the next row has
// at least one number or date, and no label shows up again in its column
// below (a name repeated in the data is a value, not a header). A file with
// a single row has no header. The user confirms the guess before any column
// name is shared with the AI (see AiPanel).
export const guessHeaderRow = (cells) => {
  const first = cells.findIndex((row) => row.some((v) => !isBlank(v)));
  if (first === -1) return { hasHeader: false, headerRow: 1 };
  const noHeader = { hasHeader: false, headerRow: first + 1 };
  const filled = cells[first].filter((v) => !isBlank(v));
  const next = cells[first + 1] || [];
  const allLabels = filled.length > 0 && filled.every(looksLikeLabel);
  const nextHasData = next.some((v) => !isBlank(v) && !looksLikeLabel(v));
  const distinct = new Set(filled.map((v) => toText(v).toLowerCase())).size === filled.length;
  if (!allLabels || !distinct || !nextHasData) return noHeader;
  const below = cells.slice(first + 1, first + 201);
  const repeated = cells[first].some((label, index) => !isBlank(label) && below.some((row) => toText(row[index]).toLowerCase() === toText(label).toLowerCase()));
  return repeated ? noHeader : { hasHeader: true, headerRow: first + 1 };
};

const dataStart = ({ hasHeader, headerRow = 1, dataStartRow }) =>
  (dataStartRow ?? (hasHeader ? headerRow + 1 : headerRow)) - 1;

const sheetWidth = (cells) => cells.reduce((max, row) => Math.max(max, row.length), 0);

// The file's columns as found: [{ index, letter, header, name }].
export const fileColumns = (cells, settings) => {
  const headerCells = settings.hasHeader ? cells[(settings.headerRow || 1) - 1] || [] : [];
  const taken = new Set();
  return Array.from({ length: sheetWidth(cells) }, (_, index) => {
    const letter = columnLetter(index);
    const header = isBlank(headerCells[index]) ? null : toText(headerCells[index]);
    const name = uniqueName(header ?? letter, taken);
    taken.add(name);
    return { index, letter, header, name };
  });
};

// Indexes (0-based) of the file rows that are data.
const dataRowIndexes = (cells, settings) => {
  const indexes = [];
  for (let i = dataStart(settings); i < cells.length; i++) if (!settings.skipBlankRows || cells[i].some((v) => !isBlank(v))) indexes.push(i);
  return indexes;
};

const dataRows = (cells, settings) => dataRowIndexes(cells, settings).map((i) => cells[i]);

// Where a bound source's rows sit in its file, for showing the file as it is:
// `numbers` is the 1-based file row of each table row, `leading` the rows
// above the data (at most `maxLeading`, the header row marked), with values
// in the order of `columns`. Steps never see the leading rows.
export const sourceRowLayout = (cells, source, columns, { maxLeading = 200 } = {}) => {
  const settings = { skipBlankRows: true, ...source };
  const start = Math.min(dataStart(settings), cells.length);
  const headerIndex = settings.hasHeader ? (settings.headerRow || 1) - 1 : -1;
  const positions = columns.map((c) => letterToIndex(c.letter));
  const leading = [];
  for (let i = Math.max(start - maxLeading, 0); i < start; i++) {
    leading.push({ number: i + 1, header: i === headerIndex, values: positions.map((p) => cells[i]?.[p] ?? null) });
  }
  return { numbers: Int32Array.from(dataRowIndexes(cells, settings), (i) => i + 1), leading };
};

// Builds a source definition from a sheet: column names, letters, headers,
// guessed types and a few sample values (handy for the UI and the AI).
export const describeSheet = (cells, settings = guessHeaderRow(cells)) => {
  const resolved = { skipBlankRows: true, ...settings };
  const rows = dataRows(cells, resolved);
  const columns = fileColumns(cells, resolved).map(({ index, letter, header, name }) => {
    const values = rows.map((row) => row[index]);
    return {
      name,
      letter,
      header,
      type: inferType(values),
      samples: values.filter((v) => !isBlank(v)).slice(0, 5),
    };
  });
  return { ...resolved, rowCount: rows.length, columns };
};

export const createSource = ({ id, label, originalName, sheetName, matchBy, ...settings }, cells) => {
  const description = describeSheet(cells, Object.keys(settings).length ? settings : guessHeaderRow(cells));
  return {
    id,
    label: label || originalName || id,
    originalName,
    sheetName,
    matchBy: matchBy || (description.hasHeader ? "header" : "position"),
    hasHeader: description.hasHeader,
    headerRow: description.headerRow,
    dataStartRow: settings.dataStartRow,
    skipBlankRows: description.skipBlankRows,
    columns: description.columns.map(({ samples, ...column }) => column),
  };
};

// ---------- Binding a (new) file to a source ----------

const normalizeHeader = (header) => toText(header).trim().toLowerCase().replace(/\s+/g, " ");

// Loads `cells` into a table shaped like `source`. Each saved column is
// found by header or by letter (per source.matchBy, or `columnOverrides`
// { [columnName]: letter } chosen by the user). Returns issues instead of
// guessing:
//   error   – a saved column can't be found
//   warning – matched by position but the header text changed
//   info    – the file has extra columns (passed through)
// Same tests inferType used to guess the saved types.
const TYPE_CHECKS = {
  number: { words: "numbers", fits: (v) => toNumber(v) !== null && !(v instanceof Date) },
  date: { words: "dates", fits: (v) => toDate(v) !== null },
  boolean: { words: "true/false values", fits: (v) => toBoolean(v) !== null },
};

// Warnings for columns whose values mostly don't fit the type saved with the
// workflow (e.g. a "date" column with no dates in a new file). Only a
// warning: types are guesses, so the user decides whether to go on.
export const checkColumnTypes = (source, table, { sampleSize = 1000, threshold = 0.5 } = {}) => {
  const where = source.label || source.id;
  return (source.columns || []).flatMap((column) => {
    const check = TYPE_CHECKS[column.type];
    if (!check || !hasColumn(table, column.name)) return [];
    let present = 0;
    let fitting = 0;
    for (const row of table.rows) {
      const value = row[column.name];
      if (isBlank(value)) continue;
      present++;
      if (check.fits(value)) fitting++;
      if (present >= sampleSize) break;
    }
    const label = column.letter ? `${column.letter} · ${column.name}` : column.name;
    if (!present) {
      return table.rows.length ? [{ level: "info", column: column.name, message: `${where}: column ${label} is empty in this file (expected ${check.words})` }] : [];
    }
    if (fitting / present >= threshold) return [];
    return [
      {
        level: "warning",
        column: column.name,
        message: `${where}: column ${label} should contain ${check.words}, but only ${fitting} of ${present} values look like ${check.words}. The results may be wrong.`,
      },
    ];
  });
};

export const bindSource = (source, cells, { columnOverrides = {} } = {}) => {
  const settings = { skipBlankRows: true, ...source };
  const found = fileColumns(cells, settings);
  const claimed = new Set();
  const issues = [];
  const where = `${source.label || source.id}`;

  const columns = [];
  (source.columns || []).forEach((column) => {
    let match;
    const override = columnOverrides[column.name];
    if (override) {
      match = found[letterToIndex(override)];
    } else if (source.matchBy === "header" && source.hasHeader && column.header) {
      match = found.find((f) => !claimed.has(f.index) && f.header && normalizeHeader(f.header) === normalizeHeader(column.header));
    } else {
      match = found[letterToIndex(column.letter)];
      if (match && column.header && match.header && normalizeHeader(match.header) !== normalizeHeader(column.header)) {
        issues.push({
          level: "warning",
          column: column.name,
          message: `${where}: column ${column.letter} was "${column.header}" but is now "${match.header}"`,
        });
      }
    }

    if (!match) {
      issues.push({
        level: "error",
        column: column.name,
        message:
          source.matchBy === "header" && column.header
            ? `${where}: column "${column.header}" was not found in the new file`
            : `${where}: column ${column.letter} does not exist in the new file`,
      });
      return;
    }
    claimed.add(match.index);
    columns.push({ name: column.name, letter: match.letter, header: match.header, type: column.type, index: match.index });
  });

  const extras = found.filter((f) => !claimed.has(f.index) && (f.header || cells.some((row) => !isBlank(row[f.index]))));
  const taken = new Set(columns.map((c) => c.name));
  extras.forEach((extra) => {
    const name = uniqueName(extra.name, taken);
    taken.add(name);
    columns.push({ name, letter: extra.letter, header: extra.header, index: extra.index });
  });
  if (extras.length && source.columns?.length) {
    issues.push({ level: "info", message: `${where}: ${extras.length} extra column(s) in the new file: ${extras.map((e) => e.header || e.letter).join(", ")}` });
  }

  const rows = dataRows(cells, settings).map((row) => Object.fromEntries(columns.map((c) => [c.name, row[c.index] ?? null])));
  const table = { columns: columns.map(({ index, ...column }) => column), rows };
  issues.push(...checkColumnTypes(source, table));
  return { ok: !issues.some((i) => i.level === "error"), table, issues };
};
